import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2 } from 'lucide-react';
import { Footer } from '../components/Footer';
import { SubpageNav } from '../components/SubpageNav';
import { SubpageCTA } from '../components/SubpageCTA';
import { fadeUp } from '../utils/animations';
import { symptomsItems, needsItems, consequences, credibilityPairs, roles, workPrinciples, results, ctaPersonas } from '../data/omrade2-data';
export function Omrade2() {
  return <div className="min-h-screen bg-background text-ink font-sans" data-id="element-484">
      <SubpageNav activePage="omrade-2" data-id="element-485" />

      <main className="pt-20" data-id="element-486">
        {/* ── HERO ── */}
        <section className="py-20 md:py-28 px-6 bg-background" data-id="element-487">
          <div className="max-w-4xl mx-auto" data-id="element-488">
            <motion.span className="text-sage font-medium tracking-widest text-xs uppercase mb-4 block" custom={0} initial="hidden" animate="visible" variants={fadeUp} data-id="element-489">
              Område 02
            </motion.span>
            <motion.h1 className="text-4xl md:text-6xl font-light text-ink leading-[1.1] mb-6" custom={1} initial="hidden" animate="visible" variants={fadeUp} data-id="element-490">
              Från familj till by –{' '}
              <span className="text-sage" data-id="element-491">när ett team blir tre</span>
            </motion.h1>
            <motion.p className="text-xl text-muted font-light max-w-2xl leading-relaxed mb-8" custom={2} initial="hidden" animate="visible" variants={fadeUp} data-id="element-492">
              Hur du får strukturen att hänga med när organisationen växer.
            </motion.p>
            <motion.div className="w-16 h-1 bg-sage rounded-full" custom={3} initial="hidden" animate="visible" variants={fadeUp} data-id="element-493" />
          </div>
        </section>

        {/* ── PROBLEM ── */}
        <section className="py-16 md:py-24 px-6 bg-card/40" data-id="element-494">
          <div className="max-w-4xl mx-auto" data-id="element-495">
            <motion.div custom={0} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeUp} className="mb-12" data-id="element-496">
              <span className="text-sage font-medium tracking-widest text-xs uppercase mb-3 block" data-id="element-497">
                01 — Problemet
              </span>
              <h2 className="text-3xl md:text-4xl font-light text-ink mb-6" data-id="element-498">
                Ni är tre team.
                <br data-id="element-499" />
                <span className="text-muted" data-id="element-500">
                  Men ni är inte tre gånger så effektiva.
                </span>
              </h2>
              <p className="text-lg text-muted leading-relaxed max-w-2xl" data-id="element-501">
                Ni började som ett sammansvetsat team. Beslut togs snabbt. Alla
                visste ungefär vad alla gjorde. Nu är ni tre team — men
                strukturen hängde inte med.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-502">
              <motion.div custom={1} initial="hidden" whileInView="visible" viewport={{
              once: true
            }} variants={fadeUp} className="bg-background rounded-2xl p-8 border border-border/50" data-id="element-503">
                <h3 className="text-sm font-medium tracking-widest uppercase text-terracotta mb-6" data-id="element-504">
                  Det märker ni på
                </h3>
                <ul className="space-y-3" data-id="element-505">
                  {symptomsItems.map((item, i) => <li key={i} className="flex items-start gap-3 text-muted" data-id="element-506">
                      <span className="w-1.5 h-1.5 rounded-full bg-terracotta mt-2 flex-shrink-0" data-id="element-507" />
                      <span className="text-sm leading-relaxed" data-id="element-508">{item}</span>
                    </li>)}
                </ul>
              </motion.div>

              <motion.div custom={2} initial="hidden" whileInView="visible" viewport={{
              once: true
            }} variants={fadeUp} className="bg-background rounded-2xl p-8 border border-border/50" data-id="element-509">
                <h3 className="text-sm font-medium tracking-widest uppercase text-sage mb-6" data-id="element-510">
                  Det ni faktiskt behöver
                </h3>
                <ul className="space-y-3" data-id="element-511">
                  {needsItems.map((item, i) => <li key={i} className="flex items-start gap-3 text-muted" data-id="element-512">
                      <span className="w-1.5 h-1.5 rounded-full bg-sage mt-2 flex-shrink-0" data-id="element-513" />
                      <span className="text-sm leading-relaxed" data-id="element-514">{item}</span>
                    </li>)}
                </ul>
              </motion.div>
            </div>

            <motion.div custom={3} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeUp} className="mt-10 bg-ink rounded-2xl px-8 py-7" data-id="element-515">
              <p className="text-white/90 text-lg leading-relaxed" data-id="element-516">
                Det är inte för att människor blivit sämre.{' '}
                <span className="text-white font-medium" data-id="element-517">
                  Det är för att ni har gått från familj till by.
                </span>{' '}
                <span className="text-white/60" data-id="element-518">
                  En familj fungerar relationellt. En by behöver struktur.
                </span>
              </p>
            </motion.div>
          </div>
        </section>

        {/* ── KONSEKVENSER ── */}
        <section className="py-16 md:py-24 px-6 bg-background" data-id="element-519">
          <div className="max-w-4xl mx-auto" data-id="element-520">
            <motion.div custom={0} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeUp} className="mb-12" data-id="element-521">
              <span className="text-sage font-medium tracking-widest text-xs uppercase mb-3 block" data-id="element-522">
                02 — Konsekvenser
              </span>
              <h2 className="text-3xl md:text-4xl font-light text-ink mb-4" data-id="element-523">
                Ni kan inte skala informell samordning
              </h2>
              <p className="text-muted text-lg" data-id="element-524">
                Det som fungerade med 10 personer fungerar inte med 30.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-id="element-525">
              {consequences.map((item, i) => <motion.div key={i} custom={i + 1} initial="hidden" whileInView="visible" viewport={{
              once: true
            }} variants={fadeUp} className="bg-card rounded-2xl p-6 border border-border/50" data-id="element-526">
                  <item.icon className="w-5 h-5 text-sage mb-4" strokeWidth={1.5} data-id="element-527" />
                  <p className="text-ink font-medium text-sm mb-1" data-id="element-528">
                    {item.text}
                  </p>
                  <p className="text-muted text-sm" data-id="element-529">{item.sub}</p>
                </motion.div>)}
            </div>

            <motion.div custom={7} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeUp} className="mt-10 text-center" data-id="element-530">
              <p className="text-xl text-ink font-light" data-id="element-531">
                Det som saknas är inte kompetens.
              </p>
              <p className="text-muted mt-2" data-id="element-532">Det är system.</p>
            </motion.div>
          </div>
        </section>

        {/* ── TROVÄRDIGHET ── */}
        <section className="py-16 md:py-24 px-6 bg-ink text-white" data-id="element-533">
          <div className="max-w-4xl mx-auto" data-id="element-534">
            <motion.div custom={0} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeUp} className="mb-12" data-id="element-535">
              <span className="text-sage font-medium tracking-widest text-xs uppercase mb-3 block" data-id="element-536">
                Några perspektiv
              </span>
              <h2 className="text-3xl md:text-4xl font-light text-white mb-4" data-id="element-537">
                Jag har byggt strukturer i miljöer där det verkligen gäller.
              </h2>
              <p className="text-white/70 text-lg max-w-2xl leading-relaxed" data-id="element-538">
                Jag har arbetat som förändringsledare i miljöer där
                tvärfunktionellt samarbete varit avgörande för resultat. Fokus
                har alltid varit att bygga in samverkan i systemet — inte
                förlita sig på att folk "bara ska veta".
              </p>
            </motion.div>

            <div className="space-y-3" data-id="element-539">
              {credibilityPairs.map((pair, i) => <motion.div key={i} custom={i + 1} initial="hidden" whileInView="visible" viewport={{
              once: true
            }} variants={fadeUp} className="flex items-center gap-4 bg-white/5 rounded-xl px-6 py-4 border border-white/10" data-id="element-540">
                  <span className="text-white font-medium text-sm flex-1 text-right" data-id="element-541">
                    {pair.left}
                  </span>
                  <span className="text-sage text-xs font-medium tracking-widest uppercase px-3" data-id="element-542">
                    →
                  </span>
                  <span className="text-white/70 text-sm flex-1" data-id="element-543">
                    {pair.right}
                  </span>
                </motion.div>)}
            </div>

            <motion.p custom={6} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeUp} className="mt-10 text-white/50 text-sm italic" data-id="element-544">
              Struktur är inte kontroll. Det är förutsättningen för att
              kompetens ska blomma.
            </motion.p>
          </div>
        </section>

        {/* ── LÖSNING ── */}
        <section className="py-16 md:py-24 px-6 bg-background" data-id="element-545">
          <div className="max-w-4xl mx-auto" data-id="element-546">
            <motion.div custom={0} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeUp} className="mb-12" data-id="element-547">
              <span className="text-sage font-medium tracking-widest text-xs uppercase mb-3 block" data-id="element-548">
                03 — Lösningen
              </span>
              <h2 className="text-3xl md:text-4xl font-light text-ink mb-4" data-id="element-549">
                Vi designar strukturen.{' '}
                <span className="text-sage" data-id="element-550">Inte bara org-schemat.</span>
              </h2>
              <p className="text-muted text-lg max-w-2xl leading-relaxed" data-id="element-551">
                Vi börjar i riktningen ni satte i Område 1 — och bygger sedan
                strukturen som gör den genomförbar i praktiken.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-14" data-id="element-552">
              {roles.map((role, i) => <motion.div key={i} custom={i + 1} initial="hidden" whileInView="visible" viewport={{
              once: true
            }} variants={fadeUp} className={`flex items-start gap-4 rounded-xl p-5 border ${role.humor ? 'bg-sage/8 border-sage/25 border-dashed' : 'bg-card border-border/50'}`} data-id="element-553">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${role.humor ? 'bg-sage/15' : 'bg-sage/10'}`} data-id="element-554">
                    <role.icon className={`w-4 h-4 ${role.humor ? 'text-sage' : 'text-sage'}`} strokeWidth={1.5} data-id="element-555" />
                  </div>
                  <span className={`text-sm leading-relaxed ${role.humor ? 'text-muted italic' : 'text-ink'}`} data-id="element-556">
                    {role.label}
                  </span>
                </motion.div>)}
            </div>

            <motion.div custom={7} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeUp} className="bg-card/60 rounded-2xl p-8 border border-border/50" data-id="element-557">
              <h3 className="text-sm font-medium tracking-widest uppercase text-ink mb-6" data-id="element-558">
                Hur vi arbetar
              </h3>
              <ul className="space-y-4" data-id="element-559">
                {workPrinciples.map((p, i) => <li key={i} className="flex items-start gap-3" data-id="element-560">
                    <CheckCircle2 className="w-4 h-4 text-sage mt-0.5 flex-shrink-0" strokeWidth={1.5} data-id="element-561" />
                    <span className="text-muted text-sm leading-relaxed" data-id="element-562">
                      {p}
                    </span>
                  </li>)}
              </ul>
              <p className="mt-8 text-ink font-medium text-sm border-t border-border/50 pt-6" data-id="element-563">
                Det handlar inte om fler möten.{' '}
                <span className="text-sage" data-id="element-564">
                  Det handlar om att bygga in samverkan i systemet.
                </span>
              </p>
            </motion.div>
          </div>
        </section>

        {/* ── RESULTAT ── */}
        <section className="py-16 md:py-24 px-6 bg-card/40" data-id="element-565">
          <div className="max-w-4xl mx-auto" data-id="element-566">
            <motion.div custom={0} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeUp} className="mb-12" data-id="element-567">
              <span className="text-sage font-medium tracking-widest text-xs uppercase mb-3 block" data-id="element-568">
                04 — Resultatet
              </span>
              <h2 className="text-3xl md:text-4xl font-light text-ink" data-id="element-569">
                Vad som händer när strukturen sitter
              </h2>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-570">
              {results.map((result, i) => {
              const isLast = i === results.length - 1;
              return <motion.div key={i} custom={i + 1} initial="hidden" whileInView="visible" viewport={{
                once: true
              }} variants={fadeUp} className={`flex items-start gap-3 rounded-xl p-5 border ${isLast ? 'bg-sage/5 border-sage/20 border-dashed' : 'bg-background border-border/50'}`} data-id="element-571">
                    {isLast ? <span className="text-sage mt-0.5 flex-shrink-0 text-base leading-none" data-id="element-572">
                        —
                      </span> : <CheckCircle2 className="w-5 h-5 text-sage mt-0.5 flex-shrink-0" strokeWidth={1.5} data-id="element-573" />}
                    <span className={`text-sm leading-relaxed ${isLast ? 'text-muted italic' : 'text-ink'}`} data-id="element-574">
                      {result}
                    </span>
                  </motion.div>;
            })}
            </div>

            <motion.div custom={7} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeUp} className="mt-12 text-center" data-id="element-575">
              <p className="text-xl text-ink font-light" data-id="element-576">
                Utan struktur för flera team slösar man tid.
              </p>
              <p className="text-muted mt-2" data-id="element-577">
                Med rätt struktur bygger man kapacitet.
              </p>
            </motion.div>
          </div>
        </section>

        {/* ── CTA ── */}
        <SubpageCTA badgeText="1 dag / vecka" badgeColor="sage" heading="Känner du igen dig?" subheading="Oavsett vilken roll du har — utmaningen är ofta densamma. Ni har växt, men strukturen har inte hängt med." personas={ctaPersonas} ctaText="Boka ett samtal – helt utan förpliktelser." ctaSubtext="Vi pratar om din situation, inte om mina PowerPoints." backLinkText="Tillbaka till alla områden" backLinkUrl="/" data-id="element-578" />
      </main>
      <Footer data-id="element-579" />
    </div>;
}